import { useCallback, useEffect, useState } from 'react';

import { useSaveUserProfileMutation } from '@/entities/user';
import { useCurrentUserQuery } from '@/shared/legacy/hooks/useCurrentUserQuery';
import { useDictionary } from '@/shared/lib/i18n';
import { toast } from '@/shared/lib/toast';
import { useGetSchedulerConfigQuery, useSaveSchedulerConfigMutation } from '../../api';
import { InterviewType } from '../../model';
import { InterviewValue } from './InterviewTypeSelection.types';
import { useInterviewForm } from './useInterviewForm';

type InterviewTypeSelectionProps = {
  initialInterviewType?: InterviewType;
  onConfirm?: (value: InterviewType, interviewValue: InterviewValue) => void;
};

export const useInterviewTypeSelection = ({ initialInterviewType, onConfirm }: InterviewTypeSelectionProps) => {
  const dictionary = useDictionary();
  const saveSchedulerConfig = useSaveSchedulerConfigMutation();
  const saveUserProfile = useSaveUserProfileMutation();
  const [selectedInterviewType, setSelectedInterviewType] = useState<InterviewType | null>(
    initialInterviewType ?? null,
  );

  const { interviewValue, setInterviewValue, errors, validateAndSetErrors } = useInterviewForm({
    selectedInterviewType,
  });

  const { data: schedulerConfig, isFetching } = useGetSchedulerConfigQuery();
  const { data: currentUser } = useCurrentUserQuery();

  useEffect(() => {
    const address = schedulerConfig?.[InterviewType.InPerson].address;

    if (address) {
      setInterviewValue((prev) => ({ ...prev, address }));
    }
  }, [schedulerConfig]);

  const handleSave = useCallback(
    async (interviewType: InterviewType, interviewValue: InterviewValue) => {
      const promises = [];

      const phoneTypeRequiresPhone = interviewType === InterviewType.InPerson || interviewType === InterviewType.Call;

      if (phoneTypeRequiresPhone && interviewValue.phone && interviewValue.phone !== currentUser?.phone_number) {
        promises.push(saveUserProfile.mutateAsync({ phoneNumber: interviewValue.phone }));
      }

      const savedAddress = schedulerConfig?.[interviewType]?.address ?? '';

      if (interviewValue.address && interviewValue.address !== savedAddress) {
        promises.push(saveSchedulerConfig.mutateAsync({ interviewType, address: interviewValue.address }));
      }

      await Promise.all(promises);
    },
    [currentUser?.phone_number, schedulerConfig, saveUserProfile, saveSchedulerConfig],
  );

  const handleValidateAndSave = async () => {
    if (!selectedInterviewType) return;
    const validationErrors = validateAndSetErrors(selectedInterviewType);
    if (validationErrors) return;

    try {
      await handleSave(selectedInterviewType, interviewValue);

      toast.success(dictionary.changesSaved);

      onConfirm?.(selectedInterviewType, interviewValue);
    } catch {
      toast.error(dictionary.somethingWentWrong);
    }
  };

  const isSaving = saveSchedulerConfig.isLoading || saveUserProfile.isLoading;

  return {
    selectedInterviewType,
    setSelectedInterviewType,
    interviewValue,
    setInterviewValue,
    errors,
    validateAndSetErrors,
    schedulerConfig,
    handleValidateAndSave,
    isSaving,
    schedulerConfigIsFetching: isFetching,
  };
};
